import { Timezone } from "@/constants/dateTime";
import { convertDateTimeFormat } from "@/helpers/dateTimeHelper";
import {
  DatasetFilter,
  DateFilter,
  EFilterOptionType,
  MinMaxFilter,
  MultipleSelectFilter
} from "@/types/dataset";
import { Filter } from "@/types/project";
import { splitDoubleColon } from "@/utils/string";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezone);

export const determineDateRangeType = (criteria: string = "") => {
  const [dateTimeRange] = criteria.split("&");
  const [startRaw, endRaw] = dateTimeRange.split("/");

  const start = startRaw?.trim() || null;
  const end = endRaw?.trim() || null;

  const isTime = (value: string | null) =>
    value !== null &&
    value.length === 5 &&
    value.includes(":") &&
    !value.includes("T");

  const isDate = (value: string | null) =>
    value !== null && value.length === 10 && value.includes("-");

  const isDateTime = (value: string | null) =>
    value !== null && value.includes("T") && !Number.isNaN(Date.parse(value));

  if (isTime(start) || isTime(end)) {
    return EFilterOptionType.TIME;
  } else if (isDateTime(start) || isDateTime(end)) {
    return EFilterOptionType.DATE_TIME;
  } else if (isDate(start) || isDate(end)) {
    return EFilterOptionType.DATE;
  }
  return EFilterOptionType.DATE_TIME;
};

// Min max filter has format like this: 1-2 ([min]-[max])
// The min and max must be a number
const determineMinMaxType = (criteria: string) => {
  const [min, max] = criteria.split("-").map((val) => {
    // Remove both commas from the string before converting to number
    return val.trim().replaceAll(",", "");
  });
  return !(Number.isNaN(Number(min)) || Number.isNaN(Number(max)));
};

export const determineFilterType = (criteria: string = "") => {
  // TODO: need to revisit this. Need to handle this when only select one adm_area
  // For example, when we select only province, the criteria will be just like this:
  // UUID::31
  // It will not be treated as multiple
  // Currently, adm_area is treated like adm_area
  if (criteria.includes("|") || criteria.includes("::")) {
    return EFilterOptionType.MULTIPLE;
  } else if (criteria.includes("/")) {
    return determineDateRangeType(criteria);
  } else if (determineMinMaxType(criteria)) {
    return EFilterOptionType.MIN_MAX;
  } else {
    return EFilterOptionType.MULTIPLE;
  }
};

const buildMultipleFilterFromResponse = (filter: Filter) => ({
  selectedOptions: filter.criteria.split("|")
});

const buildDatetimeFilterFromResponse = (filter: Filter) => {
  const [datetimeRange, timeFormat] = filter.criteria.split("&");

  const [fromDate, toDate] = datetimeRange.split("/");

  const [fromDateValue, fromTimeValue] = fromDate.split("T");
  const [toDateValue, toTimeValue] = toDate.split("T");

  return {
    time_format: timeFormat,
    dateTimeRange: {
      from: {
        date: fromDateValue !== "null" ? fromDateValue : "",
        time: fromTimeValue ? fromTimeValue.replace(":00Z", "") : ""
      },
      to: {
        date: toDateValue !== "null" ? toDateValue : "",
        time: toTimeValue ? toTimeValue.replace(":00Z", "") : ""
      },
      includeNulls: filter.include_nulls
    }
  };
};

const buildDateFilterFromResponse = (filter: Filter) => {
  const [fromDate, toDate] = filter.criteria.split("/");
  return {
    dateRange: {
      from: fromDate !== "null" ? fromDate : "",
      to: toDate !== "null" ? toDate : "",
      includeNulls: filter.include_nulls
    }
  };
};

const buildTimeFilterFromResponse = (filter: Filter) => {
  const [fromTime, toTime] = filter.criteria.split("/");
  return {
    timeRange: {
      from: fromTime !== "null" ? fromTime : "",
      to: toTime !== "null" ? toTime : "",
      includeNulls: filter.include_nulls
    }
  };
};

const buildMinMaxFilterFromResponse = (filter: Filter) => {
  const [min, max] = filter.criteria.split("-");
  return {
    minMaxRange: {
      min: min,
      max: max,
      includeNulls: filter.include_nulls
    }
  };
};

export const buildFilterFromResponse = (filter: Filter): DatasetFilter => {
  const filterType = determineFilterType(filter.criteria);
  const defaultFilterValues: DatasetFilter = {
    filterId: "",
    filterLabel: "",
    attributeId: "",
    filterType,
    time_format: null
  };

  const filterBuilderMapping = {
    [EFilterOptionType.MULTIPLE]: buildMultipleFilterFromResponse,
    [EFilterOptionType.DATE_TIME]: buildDatetimeFilterFromResponse,
    [EFilterOptionType.DATE]: buildDateFilterFromResponse,
    [EFilterOptionType.TIME]: buildTimeFilterFromResponse,
    [EFilterOptionType.MIN_MAX]: buildMinMaxFilterFromResponse
  };

  return {
    ...defaultFilterValues,
    ...filterBuilderMapping[filterType](filter),
    /** Preserve `is_multiple_adm_area` from the BE filter so the downstream
     * payload-builder (`buildMultipleFilterAsPayload`) keeps ALL selected
     * admin area IDs when fetching. Without this every caller that maps a
     * saved filter back into a `DatasetFilter` (handleCatchmentDatasetOnOpenProject,
     * buildDatasetFilter, mapDatasetFilters) would silently collapse a
     * multi-area admin filter down to its last entry on re-fetch.
     */
    is_multiple_adm_area: filter.is_multiple_adm_area ?? false
  };
};

const buildMultipleFilterAsPayload = (filterPayload: DatasetFilter) => {
  // Get selected options excluding invalid ones
  const validSelectedOptions = (filterPayload.selectedOptions ?? []).filter(
    (option) => !filterPayload.invalidOptions?.includes(option)
  );

  if (validSelectedOptions.some((option) => option.includes("::"))) {
    if (filterPayload.is_multiple_adm_area) {
      return validSelectedOptions.map((option) => {
        const splitResult = splitDoubleColon(option, { id: "", code: "" });
        return splitResult.code;
      });
    } else {
      const lastOption = validSelectedOptions[validSelectedOptions.length - 1];
      const splitResult = splitDoubleColon(lastOption, {
        id: "",
        code: ""
      });
      return [splitResult.code];
    }
  }
  return validSelectedOptions;
};

const buildDatetimeFilterAsPayload = (
  filterPayload: DatasetFilter,
  timezone?: Timezone
) => {
  const { from, to, includeNulls } = filterPayload.dateTimeRange ?? {};
  return {
    start:
      from?.date && from?.time
        ? convertDateTimeFormat(
            from.date,
            from.time,
            timezone,
            filterPayload.time_format
          )
        : null,
    end:
      to?.date && to?.time
        ? convertDateTimeFormat(
            to.date,
            to.time,
            timezone,
            filterPayload.time_format
          )
        : null,
    include_nulls: includeNulls ?? false
  };
};

const buildDateFilterAsPayload = (filterPayload: DatasetFilter) => {
  const { from, to, includeNulls } = filterPayload.dateRange ?? {};
  return {
    start: from ? `${from}T00:00:00.000Z` : null,
    end: to ? `${to}T00:00:00.000Z` : null,
    include_nulls: includeNulls ?? false
  };
};

const buildTimeFilterAsPayload = (
  filterPayload: DatasetFilter,
  tz?: Timezone
) => {
  const { from, to, includeNulls } = filterPayload.timeRange ?? {};

  const BASE_DATE = "1970-01-01";

  const convertTimeToUTC = (
    time: string | undefined,
    isOvernight: boolean
  ): string | null => {
    if (!time) return null;

    const date = isOvernight
      ? dayjs(BASE_DATE).add(1, "day").format("YYYY-MM-DD")
      : BASE_DATE;

    if (!tz?.code) {
      // No timezone: treat as UTC (original behavior)
      return `${date}T${time}:00.000Z`;
    }

    // Interpret time in user's timezone and convert to UTC
    const localTime = dayjs.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", tz.code);
    return localTime.utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
  };

  // Determine if the "to" time crosses midnight (overnight scenario)
  // e.g., from: "23:00", to: "01:00" → to is on the next day
  const isOvernightTo = !!(from && to && to < from);

  return {
    start: convertTimeToUTC(from, false),
    end: convertTimeToUTC(to, isOvernightTo),
    include_nulls: includeNulls ?? false
  };
};

const buildMinMaxFilterAsPayload = (filterPayload: DatasetFilter) => {
  // Remove commas before parsing to handle numbers like "35,000,000"
  const minStr = filterPayload?.minMaxRange?.min?.replaceAll(",", "");
  const maxStr = filterPayload?.minMaxRange?.max?.replaceAll(",", "");
  const min = minStr ? Number.parseFloat(minStr) : null;
  const max = maxStr ? Number.parseFloat(maxStr) : null;

  return {
    min,
    max,
    include_nulls: filterPayload?.minMaxRange?.includeNulls ?? false
  };
};

export const buildFilterAsDatasetPayload = (
  filterPayload: DatasetFilter,
  timezone?: Timezone
): MultipleSelectFilter | DateFilter | MinMaxFilter => {
  const filterBuilderMapping = {
    [EFilterOptionType.MULTIPLE]: () =>
      buildMultipleFilterAsPayload(filterPayload),
    [EFilterOptionType.DATE_TIME]: () =>
      buildDatetimeFilterAsPayload(filterPayload, timezone),
    [EFilterOptionType.DATE]: () => buildDateFilterAsPayload(filterPayload),
    [EFilterOptionType.TIME]: () =>
      buildTimeFilterAsPayload(filterPayload, timezone),
    [EFilterOptionType.MIN_MAX]: () => buildMinMaxFilterAsPayload(filterPayload)
  };
  return filterBuilderMapping[filterPayload.filterType]
    ? filterBuilderMapping[filterPayload.filterType]()
    : [];
};
